import { Button, Chip, makeStyles, Paper, Tooltip, Typography } from "@material-ui/core";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { MdCheckCircle, MdError, MdWarning } from "react-icons/md";
import { GROUPS } from "../accounts";
import { updateAccountGroups } from "../pages/User/saveAccount";
import { IssueAction, SetupIssue } from "../setupCheck";
import { useSysadminData } from "../SysadminData";
import { errorMessage } from "../sysadminUtils";
import { ConfirmDialog } from "./ConfirmDialog";

const useStyles = makeStyles(theme => ({
    card: {
        padding: theme.spacing(1.5, 3),
        marginBottom: theme.spacing(3),
    },
    issue: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 0),
        borderTop: `1px solid ${theme.palette.divider}`,
        "&:first-of-type": {
            borderTop: "none",
        },
    },
    icon: {
        flexShrink: 0,
        marginTop: 2,
    },
    body: {
        flexGrow: 1,
        minWidth: 0,
    },
    emails: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        marginTop: 4,
    },
    allGood: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        color: theme.palette.success.main,
        padding: theme.spacing(0.5, 0),
    },
}));

const MAX_EMAILS = 6;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const actionLabels: Record<IssueAction["kind"], string> = {
    editCompany: "Add admin", addOffice: "Add office", editOffice: "Add admin", addEaterGroup: "Add eater group",
    editEaterGroup: "Edit eater group", addMembers: "Add users", makeEaters: "Make eaters", repairGroups: "Repair",
};

type BulkFix = { kind: "makeEaters" | "repairGroups"; emails: string[] };

interface Props {
    issues: SetupIssue[];
    okText: string;
    /** Runs the actions that are not bulk fixes of user groups. */
    onAction: (action: IssueAction) => void;
    onOpenUser: (email: string) => void;
}

/** What is missing or wrong, each with a button to fix it. */
export const SetupIssues: React.FC<Props> = ({ issues, okText, onAction, onOpenUser }) => {
    const classes = useStyles();
    const data = useSysadminData();
    const [bulkFix, setBulkFix] = useState<BulkFix>();
    const hasAccount = (email: string) => data.serviceUsers.some(s => s.content.id === email);

    function run(issue: SetupIssue, action: IssueAction) {
        if (action.kind === "makeEaters" || action.kind === "repairGroups") setBulkFix({ kind: action.kind, emails: issue.emails ?? [] });
        else onAction(action);
    }

    async function applyBulkFix(fix: BulkFix) {
        const failed: string[] = [];
        for (const email of fix.emails) {
            try {
                await updateAccountGroups(email, groups => (fix.kind === "makeEaters" && !groups.includes(GROUPS.eater) ? [...groups, GROUPS.eater] : groups));
            } catch (e) {
                failed.push(`${email}: ${errorMessage(e)}`);
            }
        }
        await data.reload();
        setBulkFix(undefined);
        const done = fix.emails.length - failed.length;
        if (done) toast.success(`${fix.kind === "makeEaters" ? "Made" : "Repaired"} ${plural(done, "user", "users")}`);
        if (failed.length) toast.error(`Failed for ${failed.join(", ")}`, { duration: 10000 });
    }

    return (
        <Paper variant="outlined" className={classes.card}>
            {issues.length === 0 && (
                <div className={classes.allGood}><MdCheckCircle size={20} /><Typography variant="body2">{okText}</Typography></div>
            )}
            {issues.map(issue => (
                <div key={issue.key} className={classes.issue}>
                    {issue.severity === "error"
                        ? <MdError size={20} color="#d32f2f" className={classes.icon} />
                        : <MdWarning size={20} color="#ed6c02" className={classes.icon} />}
                    <div className={classes.body}>
                        <Typography variant="body2">{issue.text}</Typography>
                        {issue.emails && (
                            <div className={classes.emails}>
                                {issue.emails.slice(0, MAX_EMAILS).map(email => hasAccount(email)
                                    ? <Chip key={email} size="small" variant="outlined" label={email} onClick={() => onOpenUser(email)} />
                                    : <Chip key={email} size="small" variant="outlined" label={email} />)}
                                {issue.emails.length > MAX_EMAILS && (
                                    <Tooltip title={issue.emails.slice(MAX_EMAILS).join(", ")}>
                                        <Chip size="small" label={`+${issue.emails.length - MAX_EMAILS}`} />
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>
                    {issue.action && (
                        <Button size="small" color="primary" onClick={() => run(issue, issue.action!)}>{issue.actionLabel ?? actionLabels[issue.action.kind]}</Button>
                    )}
                </div>
            ))}
            {bulkFix && (
                <ConfirmDialog
                    open
                    title={bulkFix.kind === "makeEaters" ? "Make users eaters" : "Repair user groups"}
                    message={<>
                        {bulkFix.kind === "makeEaters"
                            ? "The user group Eater is added, so these users can book in their eater group:"
                            : "The user groups are saved again in the correct format. No groups are added or removed:"}
                        <ul>{bulkFix.emails.map(e => <li key={e}>{e}</li>)}</ul>
                    </>}
                    confirmLabel={bulkFix.kind === "makeEaters" ? `Make ${plural(bulkFix.emails.length, "eater", "eaters")}` : "Repair"}
                    onConfirm={() => applyBulkFix(bulkFix)}
                    onClose={() => setBulkFix(undefined)}
                />
            )}
        </Paper>
    );
};

/** "OK" or the number of issues, red when one of them stops people from booking. */
export const IssueCountChip: React.FC<{ errors: number; warnings: number }> = ({ errors, warnings }) => {
    const n = errors + warnings;
    const style = n === 0 ? { background: "#e6f4ea", color: "#1e6b34" } : errors ? { background: "#fde7e7", color: "#a11a1a" } : { background: "#fff4e0", color: "#8a5300" };
    return <Chip size="small" label={n === 0 ? "OK" : plural(n, "issue", "issues")} style={{ ...style, fontWeight: 600 }} />;
};
